import { useState, useMemo } from 'react';
import { sortRows, filterRows, paginateRows } from '../../helpers';
import IconChevronDown from '../../assets/icons/icon-chevron-down.svg';
import IconChevronUp from '../../assets/icons/icon-chevron-up.svg';
import IconChevronUpDown from '../../assets/icons/icon-chevron-up-down.svg';
import Button from './Button';
import Pagination from './Pagination';
import Search from './Search';

const Table = ({ data }) => {
	const [activePage, setActivePage] = useState(1);
	const [filters, setFilters] = useState({});
	const [sort, setSort] = useState({ order: 'asc', orderBy: 'id' });
	const rowsPerPage = 10;

	const columns = [
		{ accessor: 'date', label: 'Date' },
		{ accessor: 'sunrise', label: 'Sunrise' },
		{ accessor: 'sunset', label: 'Sunset' },
	];

	const filteredRows = useMemo(() => filterRows(data, filters), [data, filters]);
	const sortedRows = useMemo(() => sortRows(filteredRows, sort), [filteredRows, sort]);
	const calculatedRows = paginateRows(sortedRows, activePage, rowsPerPage);

	const count = filteredRows.length;
	const totalPages = Math.ceil(count / rowsPerPage);

	const handleSearch = (value, accessor) => {
		setActivePage(1);

		if (value) {
			setFilters(prevFilters => ({
				...prevFilters,
				[accessor]: value,
			}));
		} else {
			setFilters(prevFilters => {
				const updatedFilters = { ...prevFilters };
				delete updatedFilters[accessor];

				return updatedFilters;
			});
		}
	};

	const handleSort = accessor => {
		setActivePage(1);
		setSort(prevSort => ({
			order: prevSort.order === 'asc' && prevSort.orderBy === accessor ? 'desc' : 'asc',
			orderBy: accessor,
		}));
	};

	return (
		<>
			<table className='table'>
				<thead className='table__header'>
					<tr className='table__row'>
						{columns.map(column => {
							const sortIcon = () => {
								if (column.accessor === sort.orderBy) {
									if (sort.order === 'asc') {
										return <img src={IconChevronUp} alt='Chevron up icon' />;
									}
									return <img src={IconChevronDown} alt='Chevron down icon' />;
								} else {
									return <img src={IconChevronUpDown} alt='Chevron up down icon' />;
								}
							};
							return (
								<th key={column.accessor} onClick={() => handleSort(column.accessor)}>
									{column.label}
									<Button type='sort' title='Click to sort column'>
										{sortIcon()}
									</Button>
								</th>
							);
						})}
					</tr>
					<tr className='table__row'>
						{columns.map(column => {
							return (
								<th key={`${column.accessor}--search`}>
									<Search placeholder={`Search ${column.label.toLowerCase()} data`} value={filters[column.accessor]} onChange={event => handleSearch(event.target.value, column.accessor)} />
								</th>
							);
						})}
					</tr>
				</thead>
				<tbody>
					{calculatedRows.map(row => {
						return (
							<tr key={row.id}>
								{columns.map(column => {
									if (column.format) {
										return (
											<td className='table__cell' key={`${column.accessor}--row`}>
												{column.format(row[column.accessor])}
											</td>
										);
									}
									return (
										<td className='table__cell' key={`${column.accessor}--column`}>
											{row[column.accessor].padStart(5, '0')}
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>

			{count > 0 ? <Pagination activePage={activePage} count={count} rowsPerPage={rowsPerPage} totalPages={totalPages} setActivePage={setActivePage} /> : <p className='no-data'>No results found. Please search again.</p>}
		</>
	);
};

export default Table;
